(function() {
  'use strict';

  // ============================================================
  // FIX: BLOCK DUPLICATE CART ADD
  // ============================================================
  (function blockDuplicateCartAdd() {
    let recentAdds = new Map();
    const origFetch = window.fetch;
    window.fetch = function(...args) {
      const url = args[0]?.toString() || '';
      if (url.includes('/cart/add')) {
        const now = Date.now();
        let variantId = null, isSelling = null;
        try {
          const body = args[1]?.body;
          if (body instanceof FormData) {
            variantId = body.get('id'); isSelling = body.get('selling_plan');
          } else if (typeof body === 'string') {
            if (body.startsWith('{') || body.startsWith('[')) {
              const parsed = JSON.parse(body);
              variantId = parsed?.id || parsed?.items?.[0]?.id;
              isSelling = parsed?.selling_plan || parsed?.items?.[0]?.selling_plan;
            } else {
              const params = new URLSearchParams(body);
              variantId = params.get('id'); isSelling = params.get('selling_plan');
            }
          }
        } catch(e) { console.warn('Could not parse cart/add body:', e); }
        if (variantId) {
          const key = `${variantId}_${isSelling || 'onetime'}`;
          const lastTime = recentAdds.get(key);
          if (lastTime && (now - lastTime) < 300) {
            console.warn('🛑 Blocked duplicate cart/add for variant:', variantId);
            return origFetch.call(this, '/cart.js');
          }
          recentAdds.set(key, now);
          setTimeout(() => recentAdds.delete(key), 1000);
        }
      }
      return origFetch.apply(this, args);
    };
    document.addEventListener('submit', function(e) {
      const form = e.target;
      if (!form.action?.includes('/cart/add')) return;
      const pfc = form.closest('product-form-component, quick-add-component, [class*="quick-add"]');
      if (pfc) { e.preventDefault(); e.stopImmediatePropagation(); }
    }, true);
    console.log('✅ Duplicate cart add protection active');
  })();

  // ============================================================
  // SHARED STATE
  // ============================================================
  const processingItems = new Set();
  let isSyncing         = false;
  let removedAddonKeys  = new Set();
  let _ownWrite         = false;

  // Stores chosen start date per item key — always writable
  const selectedStartDates = {};
  function getStartDate(itemKey) { return selectedStartDates[itemKey] || minSelectableDate(); }

  // ============================================================
  // SHARED UTILITIES
  // ============================================================
  function formatMoney(cents) {
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) return Shopify.formatMoney(cents);
    const amount = (cents / 100).toFixed(2);
    return '$' + (amount.endsWith('.00') ? amount.slice(0, -3) : amount);
  }
  function formatMoneyWithCurrency(cents) {
    const amount = (cents / 100).toFixed(2);
    return `$${amount.endsWith('.00') ? amount.slice(0, -3) : amount} USD`;
  }

  // ============================================================
  // AJAX CART API HELPERS
  // ============================================================
  async function cartGet() {
    const res = await fetch('/cart.js');
    if (!res.ok) throw new Error('Failed to get cart');
    return res.json();
  }
  async function cartAdd(items) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items })
    });
    const text = await res.text();
    if (!res.ok) throw new Error(`cart/add failed: ${res.status} - ${text}`);
    return JSON.parse(text);
  }
  async function cartChange(payload) {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    const text = await res.text();
    if (!res.ok) throw new Error(`cart/change failed: ${res.status} - ${text}`);
    return JSON.parse(text);
  }
  async function cartUpdate(updates) {
    const res = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates })
    });
    if (!res.ok) throw new Error('cart/update failed');
    return res.json();
  }

  // ============================================================
  // SAVE DATES TO SHOPIFY
  // Fires every time customer confirms a date (not just first time).
  // Writes to:
  //   1. Line item properties  → per-line in admin order detail
  //   2. Cart note             → order Notes field in admin
  // ============================================================

  function buildDateProperties(dates, planType) {
    const label = planType.charAt(0).toUpperCase() + planType.slice(1);
    const props = {
      'Delivery Plan'   : label,
      'Delivery Date 1' : formatScheduleDate(dates[0]),
    };
    if (dates[1]) props['Delivery Date 2'] = formatScheduleDate(dates[1]);
    if (dates[2]) props['Delivery Date 3'] = formatScheduleDate(dates[2]);
    return props;
  }

  function escapeRegex(str) {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  async function saveLineDates(itemKey, dates, planType) {
    const cart = await cartGet();
    const item = cart.items.find(i => i.key === itemKey);
    if (!item) return;
    // Merge date props with any existing properties (addons, etc.)
    const merged = Object.assign({}, item.properties || {}, buildDateProperties(dates, planType));
    await cartChange({ id: itemKey, quantity: item.quantity, properties: merged });
    console.log('✅ Line properties saved:', itemKey, merged);
  }

  async function saveCartNote(itemKey, productTitle, dates, planType) {
    const cart         = await cartGet();
    const existingNote = cart.note || '';
    const marker       = `[Item: ${productTitle}]`;
    const label        = planType.charAt(0).toUpperCase() + planType.slice(1);

    const newBlock = [
      marker,
      `Plan: ${label}`,
      `Delivery 1: ${formatScheduleDate(dates[0])}`,
      dates[1] ? `Delivery 2: ${formatScheduleDate(dates[1])}` : null,
      dates[2] ? `Delivery 3: ${formatScheduleDate(dates[2])}` : null,
    ].filter(Boolean).join('\n');

    let updatedNote;
    if (existingNote.includes(marker)) {
      // Replace the existing block for this product
      updatedNote = existingNote
        .replace(
          new RegExp(`\\[Item: ${escapeRegex(productTitle)}\\][\\s\\S]*?(?=\\[Item:|$)`),
          newBlock + '\n\n'
        )
        .trimEnd();
    } else {
      updatedNote = existingNote ? `${existingNote.trimEnd()}\n\n${newBlock}` : newBlock;
    }

    await fetch('/cart/update.js', {
      method : 'POST',
      headers: { 'Content-Type': 'application/json' },
      body   : JSON.stringify({ note: updatedNote })
    });
    console.log('✅ Cart note updated');
  }

  // Master save — called every time customer confirms (no lock)
   async function saveDatesToShopify(itemKey, dates, planType, productTitle) {
    try {
      // productTitle passed in directly — avoids a cartGet() race
      // where the item might already have a new key by the time we look
      const title = productTitle || 'Product';
      await saveLineDates(itemKey, dates, planType);
      await saveCartNote(itemKey, title, dates, planType);
    } catch (err) {
      console.error('❌ Failed to save delivery dates:', err);
      throw err;
    }
  }

  // ============================================================
  // DOM UPDATERS
  // ============================================================
  function domUpdateCartCount(itemCount) {
    document.querySelectorAll(
      '[data-cart-count], .cart-count, .cart__count, .cart-icon-bubble, [class*="cart-count"]'
    ).forEach(el => { el.textContent = itemCount; });
  }
  async function domUpdateCartTotal(totalPrice) {
    const cartTotalEl = document.querySelector('text-component[ref="cartTotal"]');
      console.log("totalPrice" , totalPrice)
    if (cartTotalEl) {
         let addOns = document.querySelectorAll(".cart-items__table-row--addon");
    let noAddOns = document.querySelectorAll("tbody .cart-items__table-row:not(.cart-items__table-row--addon)");
    let available_subscription = false
    if(noAddOns.length > 0) {
      noAddOns.forEach((addOn) => {
        let selectedValue = addOn.querySelector(".delivery-plan-card.active");
        if(selectedValue?.dataset.plan != "onetime") {
          console.log("noAddOn" , addOn)
            available_subscription = true
        }
      })
    }
    console.log("noAddOn" , noAddOns)
    if (addOns.length > 0 && available_subscription) {
        addOns.forEach((addOn) => {

    // Find element having the attribute
    let PriceContainer = addOn.querySelector("[data-one_time_price]");

    console.log("PriceContainer", PriceContainer);

    if (!PriceContainer) return;

    // Get attribute value
    let oneTimePrice = PriceContainer.getAttribute("data-one_time_price");

    // Convert to number and subtract
    totalPrice = totalPrice - parseInt(oneTimePrice);
console.log("oneTimePrice3" , oneTimePrice)
console.log("totalPrice3" , totalPrice)
  });
    }
      const f = formatMoneyWithCurrency(totalPrice);
      cartTotalEl.setAttribute('value', f); cartTotalEl.textContent = f;
    }
    const plain = formatMoney(totalPrice);
    document.querySelectorAll('.cart__subtotal-price, [data-cart-subtotal], [data-cart-total]')
      .forEach(el => { el.textContent = plain; });
  }
  function domUpdateRowPrice(itemKey, finalPrice, finalLinePrice) {
    const row = document.querySelector(
      `.cart-items__table-row[data-key="${itemKey}"],
       .cart-item[data-key="${itemKey}"],
       [data-cart-item-key="${itemKey}"]`
    );
    let orignalPrice = finalPrice;
    if (!row) return;
    const priceCell = row.querySelector('.cart-items__price text-component, [class*="price"] text-component');

    let addOns = document.querySelectorAll(".cart-items__table-row--addon");
    let noAddOns = document.querySelectorAll("tbody .cart-items__table-row:not(.cart-items__table-row--addon)");
    let available_subscription = false
    if(noAddOns.length > 0) {
      noAddOns.forEach((addOn) => {
        let selectedValue = addOn.querySelector(".delivery-plan-card.active");
        if(selectedValue?.dataset.plan != "onetime") {
          console.log("noAddOn" , addOn)
            available_subscription = true
        }
      })
    }
    console.log("noAddOn" , noAddOns)
    if (addOns.length > 0 && available_subscription) {

let activePlan = row.querySelector(".delivery-plan-card.active")?.dataset.plan;
console.log("activePlan")
if (row.classList.contains("cart-items__table-row--addon") && activePlan == "onetime" ) {
 
  finalLinePrice = finalLinePrice * 0.85;
  let planPrice = row.querySelector("[data-plan='onetime'] .plan-price");
  let plantext =  row.querySelector("[data-plan='onetime'] .plan-badge");
  plantext.innerText = "ONE-TIME & SAVE UP TO 15%";
  console.log("planPrice" , planPrice , finalLinePrice)
if (planPrice) {
  planPrice.innerText = `${formatMoney(finalLinePrice)}/box`;
}
}
    }
    else if(row.classList.contains("cart-items__table-row--addon")){
  let planPrice = row.querySelector("[data-plan='onetime'] .plan-price");
  let plantext =  row.querySelector("[data-plan='onetime'] .plan-badge");
  plantext.innerText = "ONE-TIME";
  console.log("planPrice" , planPrice , finalLinePrice)
  if (planPrice) {
  planPrice.innerText = `${formatMoney(finalLinePrice)}/box`;
}
    }

    console.log("row" , row)
    if (priceCell) {
      const f = formatMoney(finalLinePrice);
      let activePlan = row.querySelector(".delivery-plan-card.active")?.dataset.plan;
       if (addOns.length > 0 && available_subscription && row.classList.contains("cart-items__table-row--addon") && activePlan == "onetime") {
          priceCell.setAttribute('value', f); priceCell.innerHTML = `<span class="withoutdiscount">${formatMoney(orignalPrice)}</span> ${f}`;
       } else {
        priceCell.setAttribute('value', f); priceCell.textContent = f;
       }
      
    }
    const unitPriceEl = row.querySelector('[class*="unit-price"], [class*="item-price"]');
    if (unitPriceEl) unitPriceEl.textContent = formatMoney(finalPrice);
  }
  function domUpdateSellingPlanLabel(itemKey, sellingPlanAllocation) {
    const row = document.querySelector(
      `.cart-items__table-row[data-key="${itemKey}"], .cart-item[data-key="${itemKey}"]`
    );
    if (!row) return;
    const labelEl = row.querySelector('[class*="selling-plan"], [class*="subscription"], .cart-item__selling-plan');
    if (labelEl) labelEl.textContent = sellingPlanAllocation ? sellingPlanAllocation.selling_plan.name : 'One-time purchase';
  }
  function domHideRow(key) {
    const row = document.querySelector(
      `.cart-items__table-row[data-key="${key}"], .cart-item[data-key="${key}"], [data-cart-item-key="${key}"]`
    );
    if (row) row.style.cssText = 'display:none!important;opacity:0;height:0;overflow:hidden;margin:0;padding:0;';
  }
  function domShowRow(key) {
    const row = document.querySelector(
      `.cart-items__table-row[data-key="${key}"], .cart-item[data-key="${key}"], [data-cart-item-key="${key}"]`
    );
    if (row) row.style.cssText = '';
  }
  function domUpdateAddonSummary(addonItems) {
    _ownWrite = true;
    const existing = document.querySelector('.cart-addons-summary');
    if (existing) existing.remove();
    _ownWrite = false;
    if (!addonItems || addonItems.length === 0) return;
    const totalContainer = document.querySelector('.cart__total-container');
    if (!totalContainer) return;
    const addonTotal = addonItems.reduce((sum, item) => sum + item.final_line_price, 0);
    const summary = document.createElement('div');
    summary.className = 'cart-addons-summary cart__summary-item';
    summary.style.cssText = 'display:flex;justify-content:space-between;align-items:center;padding:12px 0;border-bottom:1px solid #e0e0e0;margin-bottom:8px;';
    summary.innerHTML = `
      <span class="cart-primary-typography">Upgrades (${addonItems.length})</span>
      <span class="cart-secondary-typography">${formatMoney(addonTotal)}</span>`;
    _ownWrite = true;
    totalContainer.insertBefore(summary, totalContainer.firstChild);
    _ownWrite = false;
  }
  // ============================================================
  // RE-RENDER DISCOUNT / SUBTOTAL BLOCK FROM SHOPIFY
  // ============================================================
  function getCartSectionId() {
    const discountEl = document.querySelector('cart-discount-component[data-section-id]');
    if (discountEl) return discountEl.dataset.sectionId;
    const sectionEl = document.querySelector('.cart__summary-totals')?.closest('[id^="shopify-section-"]');
    return sectionEl ? sectionEl.id.replace('shopify-section-', '') : null;
  }

  async function domRefreshDiscountBlock() {
    const sectionId = getCartSectionId();
    const current   = document.querySelector('.cart__original-total-container');
    if (!sectionId || !current) return;

    try {
      const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      const res  = await fetch(`${root}cart?section_id=${encodeURIComponent(sectionId)}`);
      if (!res.ok) return;
      const html  = await res.text();
      const doc   = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector('.cart__original-total-container');
      if (!fresh) return;

      _ownWrite = true;
      current.replaceWith(fresh);
      _ownWrite = false;
    } catch (err) {
      console.error('domRefreshDiscountBlock error:', err);
    }
  }
  // ============================================================
  // SUBSCRIPTION SAVINGS
  // ============================================================
  function domUpdateSubscriptionSavings(cart) {
    _ownWrite = true;
    const existing = document.querySelector('.cart-subscription-savings');
    if (existing) existing.remove();
    _ownWrite = false;
    let totalSavings = 0;
    cart.items.forEach(item => {
      if (!item.selling_plan_allocation) return;
      const compareAt = item.selling_plan_allocation.compare_at_price;
      const actualPrice = item.selling_plan_allocation.price;
      if (compareAt && compareAt > actualPrice) totalSavings += (compareAt - actualPrice) * item.quantity * 3;
    });
//     let newcart = await cartGet();
// console.log("Cart", newcart);
    let addOns = document.querySelectorAll(".cart-items__table-row--addon");

if (addOns.length > 0) {
  addOns.forEach((addOn) => {

    // Find element having the attribute
    let PriceContainer = addOn.querySelector("[data-one_time_price]");

    console.log("PriceContainer", PriceContainer);

    if (!PriceContainer) return;

    // Get attribute value
    let oneTimePrice = PriceContainer.getAttribute("data-one_time_price");

    // Convert to number and subtract
    // totalSavings = totalSavings + parseInt(oneTimePrice);
console.log("oneTimePrice" , oneTimePrice)
console.log("totalSavings" , totalSavings)
  });
}
 
    if (totalSavings <= 0) return;
    const totalContainer = document.querySelector('.cart__total-container');
    if (!totalContainer) return;
    const el = document.createElement('div');
    el.className = 'cart-subscription-savings cart__summary-item';
    el.style.cssText = 'display:flex;justify-content:space-between;align-items:center;padding:12px;margin:8px 0;background-color:#e8f5e9;border:1px solid #4CAF50;border-radius:4px;';
    el.innerHTML = `
      <span style="color:#2e7d32;font-weight:600;font-size:14px;">💰 Subscription Savings</span>
      <span style="color:#2e7d32;font-weight:700;font-size:16px;">-${formatMoney(totalSavings)}</span>`;
    _ownWrite = true;
    const addonsSummary = document.querySelector('.cart-addons-summary');
    if (addonsSummary) addonsSummary.insertAdjacentElement('afterend', el);
    else totalContainer.insertBefore(el, totalContainer.firstChild);
    _ownWrite = false;
  }

  // ============================================================
  // CENTRAL REFRESH — debounced
  // ============================================================
  let _refreshTimer = null;
  function refreshCartSavings() {
    clearTimeout(_refreshTimer);
    _refreshTimer = setTimeout(() => {
      cartGet().then(cart => {
        const addonItems = cart.items.filter(i => i.properties && i.properties._is_addon === 'true');
        console.log("addonItems" , addonItems , cart)
        domUpdateAddonSummary(addonItems);
        domUpdateSubscriptionSavings(cart);
        domUpdateCartCount(cart.item_count);
        domRenderSavingsBreakdown(cart);
        domUpdateCartTotal(cart.total_price);
        domRefreshDiscountBlock();  
      }).catch(err => console.error('refreshCartSavings error:', err));
    }, 400);
  }

  function domUpdateCheckbox(variantId, parentItemKey, isChecked) {
    const checkbox = document.querySelector(
      `.upgrade-checkbox[data-addon-variant-id="${variantId}"][data-parent-item-key="${parentItemKey}"]`
    );
    if (!checkbox) return;
    checkbox.checked = isChecked; checkbox.disabled = false;
    const upgradeItem = checkbox.closest('.upgrade-item');
    if (upgradeItem) {
      upgradeItem.style.opacity = '1';
      upgradeItem.style.background  = isChecked ? '#e8f5e9' : '';
      upgradeItem.style.borderColor = isChecked ? '#4CAF50' : '';
    }
  }

  function domApplyCartState(cart) {
    domUpdateCartCount(cart.item_count);
    domUpdateCartTotal(cart.total_price);
    const addonItems = cart.items.filter(i => i.properties && i.properties._is_addon === 'true');
    const addonKeys  = new Set(addonItems.map(i => i.key));
    cart.items.forEach(item => {
      if (addonKeys.has(item.key)) domHideRow(item.key);
      else domUpdateRowPrice(item.key, item.final_price, item.final_line_price);
    });
    document.querySelectorAll('.cart-items__table-row[style*="display"], .cart-item[style*="display"]')
      .forEach(row => {
        const key = row.getAttribute('data-key') || row.getAttribute('data-cart-item-key');
        if (key && !addonKeys.has(key) && !removedAddonKeys.has(key)) domShowRow(key);
      });
    domUpdateAddonSummary(addonItems);
    domUpdateSubscriptionSavings(cart);
    const activeAddons = new Map();
    addonItems.forEach(item => activeAddons.set(`${item.variant_id}_${item.properties._addon_for}`, true));
    document.querySelectorAll('.upgrade-checkbox').forEach(checkbox => {
      const vId = checkbox.dataset.addonVariantId, pKey = checkbox.dataset.parentItemKey;
      const isActive = activeAddons.has(`${vId}_${pKey}`);
      const upgradeItem = checkbox.closest('.upgrade-item');
      checkbox.checked = isActive;
      if (upgradeItem) {
        upgradeItem.style.background  = isActive ? '#e8f5e9' : '';
        upgradeItem.style.borderColor = isActive ? '#4CAF50' : '';
      }
    });
  }

  // ============================================================
  // INTERCEPT CART-MUTATING FETCHES
  // ============================================================
  (function interceptCartFetch() {
    const CART_MUTATING = ['/cart/change', '/cart/update', '/cart/clear', '/cart/add'];
    const prevFetch = window.fetch;
    window.fetch = function(...args) {
      const url = args[0]?.toString() || '';
      const isMutating = CART_MUTATING.some(p => url.includes(p));
      const promise = prevFetch.apply(this, args);
      if (isMutating) promise.then(r => { if (r.ok) refreshCartSavings(); return r; }).catch(() => {});
      return promise;
    };
  })();

  // ============================================================
  // DELIVERY SCHEDULE — DATE HELPERS
  // ============================================================
  const SCHED_DAYS   = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
  const SCHED_MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const FULL_MONTHS  = ['January','February','March','April','May','June',
                        'July','August','September','October','November','December'];

  // ── Add/remove holiday dates here (YYYY-MM-DD) ──────────────
  const HOLIDAY_SET = new Set([
    '2025-11-27',
    '2025-12-25',
    '2026-01-01',
    '2026-07-04',
    '2026-11-26',
    '2026-12-25',
  ]);

  function addDays(date, n)      { const d = new Date(date); d.setDate(d.getDate() + n); return d; }
  function toYMD(date)           { return `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,'0')}-${String(date.getDate()).padStart(2,'0')}`; }
  function isHoliday(date)       { return HOLIDAY_SET.has(toYMD(date)); }
  function minSelectableDate() {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + 1); // at least tomorrow
    while (d.getDay() !== 4) d.setDate(d.getDate() + 1); // advance to next Thursday
    return d;
  }
   function skipHolidays(date) {
    let d = new Date(date);
    // First snap to a Thursday (in case caller passes a non-Thursday)
    while (d.getDay() !== 4) d.setDate(d.getDate() + 1);
    // If that Thursday is a holiday, jump to the next Thursday (not just +1 day)
    while (isHoliday(d)) d.setDate(d.getDate() + 7);
    return d;
  }
  function formatScheduleDate(date) {
    return `${SCHED_DAYS[date.getDay()].slice(0,3)}, ${SCHED_MONTHS[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
  }
   function buildScheduleDates(planType, startDate) {
    const first = skipHolidays(startDate);   // guaranteed Thursday, non-holiday
    if (planType === 'onetime') return [first];
    // 14 days = every 2 weeks (Thu → Thu), 28 days = every 4 weeks (Thu → Thu)
    const interval = planType === 'biweekly' ? 14 : 28;
    const dates = [first];
    for (let i = 1; i < 3; i++) {
      dates.push(skipHolidays(addDays(dates[i - 1], interval)));
    }
    return dates;
  }

  // ============================================================
  // CALENDAR POPUP STYLES (injected once)
  // ============================================================
  // (function injectCalendarStyles() {
  //   if (document.getElementById('ds-calendar-styles')) return;
  //   const s = document.createElement('style');
  //   s.id = 'ds-calendar-styles';
  //   s.textContent = `
  //     .ds-datepicker-wrap { margin: 10px 0 6px; }

  //     /* Trigger button — normal state */
  //     .ds-trigger {
  //       display:inline-flex;align-items:center;gap:7px;
  //       background:#fff;border:1.5px solid #e24e27;border-radius:8px;
  //       padding:7px 14px;font-size:13px;font-weight:600;color:#e24e27;
  //       cursor:pointer;transition:background .18s,color .18s;
  //     }
  //     .ds-trigger:hover { background:#e24e27; color:#fff; }
  //     .ds-trigger svg   { flex-shrink:0; }

  //     /* Saving state — while Shopify API calls are in flight */
  //     .ds-trigger--saving {
  //       opacity:.7; cursor:not-allowed; pointer-events:none;
  //     }

  //     /* Small "Saved ✓" badge that appears after confirmation */
  //     .ds-saved-badge {
  //       display:inline-block;margin-left:6px;font-size:10px;font-weight:700;
  //       background:#4CAF50;color:#fff;padding:2px 7px;border-radius:10px;
  //       letter-spacing:.04em;vertical-align:middle;
  //       animation:ds-badge-in .25s ease;
  //     }
  //     @keyframes ds-badge-in { from{opacity:0;transform:scale(.8)} to{opacity:1;transform:scale(1)} }

  //     /* Overlay & popup */
  //     .ds-overlay {
  //       position:fixed;inset:0;background:rgba(0,0,0,.35);z-index:9998;
  //       display:flex;align-items:center;justify-content:center;
  //       animation:ds-fade-in .15s ease;
  //     }
  //     @keyframes ds-fade-in { from{opacity:0} to{opacity:1} }
  //     .ds-calendar {
  //       background:#fff;border-radius:16px;box-shadow:0 8px 40px rgba(0,0,0,.18);
  //       padding:24px;width:320px;max-width:95vw;
  //       z-index:9999;position:relative;animation:ds-slide-up .2s ease;
  //     }
  //     @keyframes ds-slide-up {
  //       from{transform:translateY(16px);opacity:0}
  //       to{transform:translateY(0);opacity:1}
  //     }

  //     .ds-cal-header { display:flex;align-items:center;justify-content:space-between;margin-bottom:16px; }
  //     .ds-cal-header h4 { margin:0;font-size:16px;font-weight:700;color:#1a1a1a; }
  //     .ds-nav-btn {
  //       background:none;border:none;cursor:pointer;padding:4px 8px;
  //       border-radius:6px;color:#555;font-size:18px;line-height:1;transition:background .15s;
  //     }
  //     .ds-nav-btn:hover { background:#f0f0f0; }

  //     .ds-dow-row { display:grid;grid-template-columns:repeat(7,1fr);text-align:center;margin-bottom:6px; }
  //     .ds-dow { font-size:11px;font-weight:700;color:#999;padding:4px 0;text-transform:uppercase;letter-spacing:.03em; }

  //     .ds-day-grid { display:grid;grid-template-columns:repeat(7,1fr);gap:2px; }
  //     .ds-day {
  //       aspect-ratio:1;display:flex;align-items:center;justify-content:center;
  //       border-radius:50%;font-size:13px;cursor:pointer;border:none;background:none;
  //       color:#1a1a1a;transition:background .12s,color .12s;font-weight:500;
  //     }
  //     .ds-day:hover:not(.ds-day--disabled):not(.ds-day--selected):not(.ds-day--holiday) {
  //       background:#fce4ec;color:#e24e27;
  //     }
  //     .ds-day--selected  { background:#e24e27;color:#fff;font-weight:700; }
  //     .ds-day--today:not(.ds-day--selected) { border:1.5px solid #e24e27;color:#e24e27; }
  //     .ds-day--disabled  { color:#ccc;cursor:not-allowed;text-decoration:line-through; }
  //     .ds-day--holiday:not(.ds-day--selected) {
  //       background:#fff3f3;color:#e57373;cursor:not-allowed;position:relative;
  //     }
  //     .ds-day--holiday:not(.ds-day--selected)::after {
  //       content:'';position:absolute;bottom:4px;left:50%;transform:translateX(-50%);
  //       width:4px;height:4px;border-radius:50%;background:#e57373;
  //     }
  //     .ds-day--empty { pointer-events:none; }

  //     .ds-legend { display:flex;gap:12px;margin-top:12px;padding-top:12px;border-top:1px solid #f0f0f0;flex-wrap:wrap; }
  //     .ds-legend-item { display:flex;align-items:center;gap:5px;font-size:11px;color:#777; }
  //     .ds-legend-dot  { width:10px;height:10px;border-radius:50%; }

  //     .ds-cal-footer { display:flex;justify-content:flex-end;gap:8px;margin-top:16px; }
  //     .ds-btn {
  //       padding:8px 18px;border-radius:8px;font-size:13px;font-weight:600;
  //       cursor:pointer;border:1.5px solid transparent;transition:all .15s;
  //     }
  //     .ds-btn--cancel  { background:#f5f5f5;color:#555;border-color:#e0e0e0; }
  //     .ds-btn--cancel:hover { background:#ebebeb; }
  //     .ds-btn--confirm { background:#e24e27;color:#fff;border-color:#e24e27; }
  //     .ds-btn--confirm:hover { background:#c2185b; }
  //     .ds-btn--confirm:disabled { background:#ccc;border-color:#ccc;cursor:not-allowed; }

  //     .ds-saving-spinner {
  //       display:inline-block;width:12px;height:12px;border:2px solid #fff;
  //       border-top-color:transparent;border-radius:50%;
  //       animation:ds-spin .6s linear infinite;margin-left:6px;vertical-align:middle;
  //     }
  //     @keyframes ds-spin { to{transform:rotate(360deg)} }
  //   `;
  //   document.head.appendChild(s);
  // })();

  // ============================================================
  // CALENDAR POPUP — LOGIC
  // ============================================================
  let calendarState = { overlay:null, viewYear:null, viewMonth:null, pendingDate:null, onConfirm:null };

  function closeCalendar() {
    if (calendarState.overlay) { calendarState.overlay.remove(); calendarState.overlay = null; }
  }

  function openCalendar(itemKey, planType, currentStartDate, onConfirm) {
    closeCalendar();
    const minDate  = minSelectableDate();
    const viewDate = (currentStartDate && currentStartDate >= minDate) ? currentStartDate : minDate;
    calendarState  = {
      overlay: null, viewYear: viewDate.getFullYear(), viewMonth: viewDate.getMonth(),
      pendingDate: new Date(viewDate), onConfirm,
    };
    const overlay = document.createElement('div');
    overlay.className = 'ds-overlay';
    overlay.addEventListener('click', e => { if (e.target === overlay) closeCalendar(); });
    const cal = document.createElement('div');
    cal.className = 'ds-calendar';
    cal.setAttribute('role', 'dialog');
    cal.setAttribute('aria-label', 'Choose delivery start date');
    overlay.appendChild(cal);
    calendarState.overlay = overlay;
    document.body.appendChild(overlay);
    renderCalendarUI(cal);
  }

  function renderCalendarUI(cal) {
    const { viewYear, viewMonth, pendingDate } = calendarState;
    const minDate      = minSelectableDate();
    const today        = new Date(); today.setHours(0,0,0,0);
    const firstOfMonth = new Date(viewYear, viewMonth, 1);
    const startDow     = firstOfMonth.getDay();
    const daysInMonth  = new Date(viewYear, viewMonth + 1, 0).getDate();

    const dowLabels = ['Su','Mo','Tu','We','Th','Fr','Sa'].map(d => `<div class="ds-dow">${d}</div>`).join('');

    let dayCells = '';
    for (let i = 0; i < startDow; i++) dayCells += `<div class="ds-day ds-day--empty"></div>`;
    for (let day = 1; day <= daysInMonth; day++) {
      const cellDate   = new Date(viewYear, viewMonth, day);
      const isThursday = cellDate.getDay() === 4;
      const isHol      = isHoliday(cellDate);
      // Disabled if: before min date, not a Thursday, or a holiday
      const disabled   = cellDate < minDate || !isThursday || isHol;
      const isToday    = toYMD(cellDate) === toYMD(today);
      const isSelected = pendingDate && toYMD(cellDate) === toYMD(pendingDate);

      let cls = 'ds-day';
      if (isSelected)        cls += ' ds-day--selected';
      else if (isToday)      cls += ' ds-day--today';
      if (cellDate < minDate) cls += ' ds-day--disabled';
      else if (isHol)        cls += ' ds-day--holiday';
      else if (!isThursday)  cls += ' ds-day--disabled';

      let titleAttr = '';
      if (isHol)         titleAttr = 'title="Holiday — unavailable"';
      else if (!isThursday) titleAttr = 'title="We only deliver on Thursdays"';

      dayCells += `<button class="${cls}" data-date="${toYMD(cellDate)}"
        ${disabled ? 'disabled' : ''} ${titleAttr}
        aria-label="${SCHED_DAYS[cellDate.getDay()]} ${FULL_MONTHS[viewMonth]} ${day} ${viewYear}"
      >${day}</button>`;
    }

    cal.innerHTML = `
      <div class="ds-cal-header">
        <button class="ds-nav-btn" id="ds-prev">&#8249;</button>
        <h4>${FULL_MONTHS[viewMonth]} ${viewYear}</h4>
        <button class="ds-nav-btn" id="ds-next">&#8250;</button>
      </div>
      <div class="ds-dow-row">${dowLabels}</div>
      <div class="ds-day-grid">${dayCells}</div>
      <div class="ds-legend">
        <div class="ds-legend-item"><div class="ds-legend-dot" style="background:#e24e27"></div><span>Selected</span></div>
        <div class="ds-legend-item"><div class="ds-legend-dot" style="background:#e57373"></div><span>Holiday</span></div>
        <div class="ds-legend-item"><div class="ds-legend-dot" style="background:#ddd"></div><span>Unavailable</span></div>
      </div>
      <div class="ds-cal-footer">
        <button class="ds-btn ds-btn--cancel" id="ds-cancel">Cancel</button>
        <button class="ds-btn ds-btn--confirm" id="ds-confirm" ${!pendingDate?'disabled':''}>Confirm Date</button>
      </div>`;

    cal.querySelector('#ds-prev').addEventListener('click', () => {
      calendarState.viewMonth--;
      if (calendarState.viewMonth < 0) { calendarState.viewMonth = 11; calendarState.viewYear--; }
      renderCalendarUI(cal);
    });
    cal.querySelector('#ds-next').addEventListener('click', () => {
      calendarState.viewMonth++;
      if (calendarState.viewMonth > 11) { calendarState.viewMonth = 0; calendarState.viewYear++; }
      renderCalendarUI(cal);
    });
    cal.querySelectorAll('.ds-day:not([disabled])').forEach(btn => {
      btn.addEventListener('click', function() {
        const [y,m,d] = this.dataset.date.split('-').map(Number);
        calendarState.pendingDate = new Date(y, m-1, d);
        renderCalendarUI(cal);
      });
    });
    cal.querySelector('#ds-cancel').addEventListener('click', closeCalendar);
    cal.querySelector('#ds-confirm').addEventListener('click', async function() {
      if (!calendarState.pendingDate) return;
      const confirmed = new Date(calendarState.pendingDate);
      this.disabled = true;
      this.innerHTML = `Saving<span class="ds-saving-spinner"></span>`;
      closeCalendar();
      if (calendarState.onConfirm) await calendarState.onConfirm(confirmed);
    });
  }

  // ============================================================
  // DELIVERY SCHEDULE RENDERER
  // ============================================================
  const calendarSVG = `<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <rect x="3" y="4" width="18" height="18" rx="2" stroke="#e24e27" stroke-width="2"/>
    <path d="M16 2v4M8 2v4M3 10h18" stroke="#e24e27" stroke-width="2"/>
  </svg>`;
  const clockSVG = `<svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <circle cx="10" cy="10" r="9" stroke="#e24e27" stroke-width="2"/>
    <path d="M10 6v4l3 2" stroke="#e24e27" stroke-width="2" stroke-linecap="round"/>
  </svg>`;
  const pencilSVG = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"
          stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"
          stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
  </svg>`;

  function renderScheduleHTML(planType, startDate, showSavedBadge) {
    const dates     = buildScheduleDates(planType, startDate);
    const isOnetime = planType === 'onetime';
    const rowsHTML  = dates.map((date, i) => `
      <div class="schedule-item">
        <div class="schedule-icon">${calendarSVG}</div>
        <div class="schedule-details">
          <div class="schedule-title">Delivery #${i + 1}</div>
          <div class="schedule-date">${formatScheduleDate(date)}</div>
        </div>
        <div class="schedule-day">${SCHED_DAYS[date.getDay()]}</div>
      </div>`).join('');

    const savedBadge = showSavedBadge
      ? `<span class="ds-saved-badge">Saved ✓</span>`
      : '';

    return `
      <div class="schedule-header">
        <h4>Delivery Schedule</h4>
        <span class="schedule-count">${isOnetime ? '1 delivery' : 'Next 3 deliveries'}</span>
        ${!isOnetime ? `<span class="schedule-frequency">${planType === 'biweekly' ? 'Every 2 weeks' : 'Every 4 weeks'}</span>` : ''}
      </div>

    

      <div class="schedule-list">${rowsHTML}</div>

      <div class="schedule-footer">
        ${clockSVG}
        <span>${isOnetime
          ? 'Your one-time delivery will be made on your chosen date.'
          : 'Subsequent deliveries auto-calculated from your start date. Holidays are skipped automatically.'
        }</span>
      </div>
      ${!isOnetime ? '<p class="schedule-note">You can skip, pause, or modify your delivery schedule anytime</p>' : ''}`;
  }

 function findScheduleContainer(itemKey) {
    // Primary: find by data-item-key attribute (survives DOM re-renders)
    let container = document.querySelector(`.delivery-schedule[data-item-key="${itemKey}"]`);
    // Fallback: find by id (for initial page load before any switches)
    if (!container) container = document.getElementById(`delivery-schedule-${itemKey}`);
    return container;
  }

  function updateDeliverySchedule(itemKey, planType, startDate, showSavedBadge) {
    const container = findScheduleContainer(itemKey);
    if (!container) {
      console.warn('updateDeliverySchedule: no container for key', itemKey);
      return;
    }

    // Keep id and data-item-key in sync
    container.id               = `delivery-schedule-${itemKey}`;
    container.dataset.itemKey  = itemKey;

    const useDate = startDate || getStartDate(itemKey);
    selectedStartDates[itemKey] = useDate;

    container.innerHTML      = renderScheduleHTML(planType, useDate, !!showSavedBadge);
    container.dataset.activePlan = planType;

    const triggerBtn = container.querySelector('[data-open-calendar]');
    if (triggerBtn) {
      triggerBtn.addEventListener('click', async function() {
        // Re-read itemKey from container at click time — may have changed
        const liveKey = container.dataset.itemKey || itemKey;

        let productTitle = 'Product';
        try {
          const cart = await cartGet();
          const item = cart.items.find(i => i.key === liveKey);
          if (item) productTitle = item.product_title;
        } catch(e) { /* non-fatal */ }

        openCalendar(liveKey, planType, getStartDate(liveKey), async function(chosenDate) {
          const safeDate  = skipHolidays(chosenDate);
          const activeKey = container.dataset.itemKey || liveKey;
          const dates     = buildScheduleDates(planType, safeDate);

          const trigger = container.querySelector('[data-open-calendar]');
          if (trigger) trigger.classList.add('ds-trigger--saving');

          try {
            await saveDatesToShopify(activeKey, dates, planType, productTitle);

            selectedStartDates[activeKey] = safeDate;
            updateDeliverySchedule(activeKey, planType, safeDate, true);

            setTimeout(() => {
              const badge = container.querySelector('.ds-saved-badge');
              if (badge) { badge.style.transition = 'opacity .5s'; badge.style.opacity = '0'; }
              setTimeout(() => updateDeliverySchedule(activeKey, planType, safeDate, false), 500);
            }, 3000);

          } catch(err) {
            console.error('Date save failed:', err);
            alert('Could not save your delivery date. Please try again.');
            if (trigger) trigger.classList.remove('ds-trigger--saving');
          }
        });
      });
    }
  }


  function initAllSchedules() {
    document.querySelectorAll('.delivery-schedule[data-item-key]').forEach(container => {
      const itemKey  = container.dataset.itemKey;
      const planType = container.dataset.activePlan || 'onetime';
      // Only init if not already rendered (avoids wiping user's current state on mutation)
      if (!container.querySelector('[data-open-calendar]')) {
        updateDeliverySchedule(itemKey, planType, getStartDate(itemKey), false);
      }
    });
  }

  // ============================================================
  // DELIVERY PLAN SWITCHER
  // ============================================================
  window.updateDeliveryPlan = async function(radio) {
    const variantId   = radio.dataset.variantId;
    const sellingPlan = radio.dataset.sellingPlan;
    const planType    = radio.dataset.planType || 'onetime';
    const itemKey     = radio.dataset.itemKey;          // ← the key THIS radio belongs to
    const card        = radio.closest('.delivery-plan-card');
    const section     = radio.closest('.delivery-plan-section');
    const previousActiveCard = section.querySelector('.delivery-plan-card.active');

    section.style.opacity       = '0.5';
    section.style.pointerEvents = 'none';

    try {
      const cartBefore = await cartGet();

      // ── DIAGNOSTIC: log all items with this variant_id ──────
      const allMatches = cartBefore.items.filter(i => i.variant_id === parseInt(variantId));
      console.log(`🔍 Items with variant ${variantId}:`, allMatches.map(i => i.key));

      // ── STEP 1: Find the EXACT item by itemKey from the radio ──
      // Falls back to variant_id match if itemKey is missing/stale
      let currentItem = itemKey
        ? cartBefore.items.find(i => i.key === itemKey)
        : null;

      if (!currentItem) {
        console.warn('⚠️ itemKey not found, falling back to variant_id match');
        currentItem = cartBefore.items.find(i => i.variant_id === parseInt(variantId));
      }

      if (!currentItem) throw new Error('Item not found in cart. Please refresh the page.');

      const freshKey = currentItem.key;
      const qty      = currentItem.quantity;

      console.log('✅ Operating on item:', freshKey);

      // ── STEP 2: Remove duplicate items with same variant_id ──
      // These accumulate from failed/partial previous plan switches
      const duplicates = cartBefore.items.filter(i =>
        i.variant_id === parseInt(variantId) &&
        i.key !== freshKey &&
        !(i.properties && i.properties._is_addon === 'true')
      );
      if (duplicates.length > 0) {
        console.warn('🧹 Removing', duplicates.length, 'duplicate item(s):', duplicates.map(i => i.key));
        for (const dup of duplicates) {
          await cartChange({ id: dup.key, quantity: 0 });
        }
      }

      // ── STEP 3: Remove the current item ─────────────────────
      await cartChange({ id: freshKey, quantity: 0 });

      // ── STEP 4: Re-add with new selling plan ─────────────────
      const addItem = { id: parseInt(variantId), quantity: qty };
      if (sellingPlan && sellingPlan !== '') addItem.selling_plan = parseInt(sellingPlan);

      const addResult = await cartAdd([addItem]);
      const newItem   = addResult.items[0];
      const newKey    = newItem.key;

      console.log('✅ New item key:', newKey , addResult);

      // ── STEP 5: Migrate start date to new key ────────────────
      if (selectedStartDates[freshKey]) {
        selectedStartDates[newKey] = selectedStartDates[freshKey];
        delete selectedStartDates[freshKey];
      }

      // ── STEP 6: Update ALL radios in this section ────────────
      section.querySelectorAll('input[type="radio"]').forEach(r => {
        r.dataset.itemKey   = newKey;
        r.dataset.variantId = variantId;
      });

      // ── STEP 7: Update schedule container ───────────────────
      // Search by old key using BOTH attribute and id
      const scheduleContainer =
        document.querySelector(`.delivery-schedule[data-item-key="${freshKey}"]`) ||
        document.getElementById(`delivery-schedule-${freshKey}`);

      if (scheduleContainer) {
        scheduleContainer.id              = `delivery-schedule-${newKey}`;
        scheduleContainer.dataset.itemKey = newKey;
        console.log('✅ Container updated:', freshKey, '→', newKey);
      } else {
        // Last resort: find by section proximity
        const sectionContainer = section.nextElementSibling?.querySelector?.('.delivery-schedule')
          || section.parentElement?.querySelector?.('.delivery-schedule');
        if (sectionContainer) {
          sectionContainer.id              = `delivery-schedule-${newKey}`;
          sectionContainer.dataset.itemKey = newKey;
          console.log('✅ Container found via section proximity, updated to', newKey);
        } else {
          console.warn('⚠️ Could not find schedule container for', freshKey);
        }
      }

      // ── STEP 8: Update DOM row key ───────────────────────────
      section.querySelectorAll('.delivery-plan-card').forEach(c => c.classList.remove('active'));
      card.classList.add('active');

      const cartAfter = await cartGet();
      const freshItem = cartAfter.items.find(i => i.key === newKey);
      if (freshItem) {
        const oldRow = document.querySelector(`.cart-items__table-row[data-key="${freshKey}"]`);
        if (oldRow) {
          oldRow.setAttribute('data-key', newKey);
          oldRow.setAttribute('data-parent-key', '');
        }
        let addOns = document.querySelectorAll(".cart-items__table-row--addon");
    let noAddOns = document.querySelectorAll("tbody .cart-items__table-row:not(.cart-items__table-row--addon)");
    let available_subscription = false
    if(noAddOns.length > 0) {
      noAddOns.forEach((addOn) => {
        let selectedValue = addOn.querySelector(".delivery-plan-card.active");
        if(selectedValue?.dataset.plan != "onetime") {
          console.log("noAddOn" , addOn)
            available_subscription = true
        }
      })
    }
    console.log("noAddOn" , noAddOns)
        if(!freshItem.selling_plan_allocation && section.closest(".cart-items__table-row--addon") && available_subscription){
          let discountedPrice = freshItem.final_line_price ;
          let remainingPrice = freshItem.final_line_price * 0.15;
  
  // Example: Update the price property on the object
  freshItem.final_line_price = discountedPrice;
  console.log("discountedPrice" , discountedPrice)
  let PriceContainer = section.closest(".cart-items__table-row--addon").querySelector(".cart-items__price text-component");
  PriceContainer.setAttribute("data-one_time_price" , remainingPrice)

        }
        else if(section.closest(".cart-items__table-row--addon")){

          let PriceContainer = section.closest(".cart-items__table-row--addon").querySelector(".cart-items__price text-component");
          if (PriceContainer) {
  PriceContainer.removeAttribute("data-one_time_price");
}

        }

        domUpdateRowPrice(newKey, freshItem.final_price, freshItem.final_line_price);
        domUpdateSellingPlanLabel(newKey, freshItem.selling_plan_allocation);
        console.log("freshItem.selling_plan_allocation" , freshItem.selling_plan_allocation , freshItem.final_line_price, section.closest("tbody"))
      }

      domApplyCartState(cartAfter);

      // ── STEP 9: Render schedule (delayed to let observer settle)
      setTimeout(() => {
        updateDeliverySchedule(newKey, planType, getStartDate(newKey), false);
      }, 400);

      section.style.opacity       = '1';
      section.style.pointerEvents = 'auto';

    } catch (error) {
      console.error('Delivery plan error:', error.message);
      section.style.opacity       = '1';
      section.style.pointerEvents = 'auto';
      radio.checked = false;
      if (previousActiveCard) {
        previousActiveCard.classList.add('active');
        const prev = previousActiveCard.querySelector('input[type="radio"]');
        if (prev) prev.checked = true;
      }
      alert(`Failed: ${error.message}`);
    }
  };

  // ============================================================
  // CART ADDONS — ADD / REMOVE
  // ============================================================
  function addAddonToCart(variantId, addonName, addonPrice, parentItemKey, checkbox, upgradeItem) {
    const uniqueKey = `${variantId}_${parentItemKey}`;
    processingItems.add(uniqueKey);
    cartGet()
      .then(cart => {
        const parentItem = cart.items.find(item => item.key === parentItemKey);
        if (!parentItem) throw new Error('Parent item not found');
        return cartAdd([{ id: variantId, quantity: parentItem.quantity,
          properties: { '_addon_for': parentItemKey, '_addon_name': addonName, '_is_addon': 'true' }
        }]);
      })
      .then(addResult => {
        domHideRow(addResult.items[0].key);
        domUpdateCheckbox(variantId, parentItemKey, true);
        processingItems.delete(uniqueKey);
        return cartGet();
      })
      .then(cart => { domApplyCartState(cart); })
      .catch(error => {
        console.error('Error adding addon:', error);
        domUpdateCheckbox(variantId, parentItemKey, false);
        processingItems.delete(uniqueKey);
        alert('Error adding upgrade. Please try again.');
      });
  }

  function removeAddonFromCart(variantId, addonName, addonPrice, parentItemKey, checkbox, upgradeItem) {
    const uniqueKey = `${variantId}_${parentItemKey}`;
    processingItems.add(uniqueKey);
    cartGet()
      .then(cart => {
        const addonItem = cart.items.find(item =>
          item.variant_id.toString() === variantId.toString() &&
          item.properties && item.properties._is_addon === 'true' &&
          item.properties._addon_for === parentItemKey
        );
        if (!addonItem) {
          domUpdateCheckbox(variantId, parentItemKey, false);
          processingItems.delete(uniqueKey);
          return Promise.resolve(null);
        }
        removedAddonKeys.add(addonItem.key);
        domHideRow(addonItem.key);
        return cartChange({ id: addonItem.key, quantity: 0 });
      })
      .then(result => {
        if (!result) return null;
        domUpdateCheckbox(variantId, parentItemKey, false);
        processingItems.delete(uniqueKey);
        return cartGet();
      })
      .then(cart => {
        if (!cart) return;
        domApplyCartState(cart);
        setTimeout(() => removedAddonKeys.clear(), 500);
      })
      .catch(error => {
        console.error('Error removing addon:', error);
        domUpdateCheckbox(variantId, parentItemKey, true);
        processingItems.delete(uniqueKey);
        alert('Error removing upgrade. Please try again.');
      });
  }

  // ============================================================
  // QUANTITY SYNC
  // ============================================================
  function syncAddonQuantities() {
    if (isSyncing) return;
    isSyncing = true;
    cartGet()
      .then(cart => {
        const updates = {}; let needsUpdate = false;
        cart.items.forEach(item => {
          if (!(item.properties && item.properties._is_addon === 'true')) return;
          const parentItem = cart.items.find(p => p.key === item.properties._addon_for);
          if (parentItem) {
            if (parentItem.selling_plan_allocation) return;
            if (item.quantity !== parentItem.quantity) { updates[item.key] = parentItem.quantity; needsUpdate = true; }
          } else { updates[item.key] = 0; needsUpdate = true; }
        });
        if (needsUpdate) return cartUpdate(updates);
        return null;
      })
      .then(result => {
        if (!result) { isSyncing = false; return; }
        return cartGet().then(cart => { domApplyCartState(cart); isSyncing = false; });
      })
      .catch(error => { console.error('Sync error:', error); isSyncing = false; });
  }

  // ============================================================
  // EVENT LISTENERS
  // ============================================================
  function initUpgradeCheckboxes() {
    const checkboxes = document.querySelectorAll('.upgrade-checkbox');
    if (checkboxes.length === 0) return false;
    checkboxes.forEach(checkbox => {
      const fresh = checkbox.cloneNode(true);
      checkbox.parentNode.replaceChild(fresh, checkbox);
      fresh.addEventListener('change', function() {
        const variantId = this.dataset.addonVariantId, addonName = this.dataset.addonName,
              addonPrice = this.dataset.addonPrice, parentItemKey = this.dataset.parentItemKey,
              uniqueKey = `${variantId}_${parentItemKey}`;
        if (processingItems.has(uniqueKey)) { this.checked = !this.checked; return; }
        if (!variantId || variantId === 'undefined') { alert('Cannot add this upgrade - missing product information'); this.checked = false; return; }
        this.disabled = true;
        const upgradeItem = this.closest('.upgrade-item');
        if (upgradeItem) upgradeItem.style.opacity = '0.5';
        if (this.checked) addAddonToCart(variantId, addonName, addonPrice, parentItemKey, this, upgradeItem);
        else removeAddonFromCart(variantId, addonName, addonPrice, parentItemKey, this, upgradeItem);
      });
    });
    return true;
  }

  function watchQuantityChanges() {
    document.addEventListener('change', function(e) {
      if (e.target.matches('input[name^="delivery_plan_"]')) return;
      if (e.target.matches('input[name="updates[]"]') || e.target.matches('input[data-cart-quantity]'))
        setTimeout(syncAddonQuantities, 500);
    });
    document.addEventListener('click', function(e) {
      if (e.target.closest('.delivery-plan-section')) return;
      if (e.target.closest('.quantity-minus, .quantity-plus, button[name="minus"], button[name="plus"]'))
        setTimeout(syncAddonQuantities, 800);
    });
  }

  function watchCartDOM() {
    let debounceTimer = null;
    const observer = new MutationObserver(() => {
      if (_ownWrite) return;
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        initUpgradeCheckboxes();
        initAllSchedules();
        if (!document.querySelector('.cart-subscription-savings')) refreshCartSavings();
      }, 350);
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  // ============================================================
  // INIT
  // ============================================================
  function initSavings() {
    cartGet().then(cart => {
      const addonItems = cart.items.filter(i => i.properties && i.properties._is_addon === 'true');
      domUpdateAddonSummary(addonItems);
      domUpdateSubscriptionSavings(cart);
      domRenderSavingsBreakdown(cart);
    }).catch(err => console.error('initSavings error:', err));
  }

  function initCheckboxes() {
    if (document.querySelectorAll('.upgrade-checkbox').length === 0) return false;
    initUpgradeCheckboxes();
    cartGet().then(cart => { domApplyCartState(cart); }).catch(err => console.error('initCheckboxes error:', err));
    return true;
  }
async function cleanDuplicateCartItems() {
    try {
      const cart = await cartGet();

      // Group items by variant_id (excluding addons)
      const seen    = new Map();  // variantId → first item
      const toRemove = [];

      cart.items.forEach(item => {
        if (item.properties && item.properties._is_addon === 'true') return;
        const vid = item.variant_id;
        if (seen.has(vid)) {
          toRemove.push(item.key);
        } else {
          seen.set(vid, item.key);
        }
      });

      if (toRemove.length === 0) return;

      console.warn('🧹 Cleaning', toRemove.length, 'duplicate cart item(s) on load');
      for (const key of toRemove) {
        await cartChange({ id: key, quantity: 0 });
      }
      const cleanCart = await cartGet();
      domApplyCartState(cleanCart);
    } catch(err) {
      console.error('cleanDuplicateCartItems error:', err);
    }
  }

  function init() {


    console.log('=== Initializing cart script ===');
    initSavings();
    initCheckboxes();
    initAllSchedules();
    watchQuantityChanges();
    watchCartDOM();
    console.log('✓ Cart script initialized');
    cleanDuplicateCartItems();
  }

  const style = document.createElement('style');
  style.textContent = `.upgrade-item{transition:all .3s ease}.upgrade-checkbox{cursor:pointer}.upgrade-checkbox:disabled{cursor:not-allowed}`;
  document.head.appendChild(style);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  // ============================================================
  // SAVINGS BREAKDOWN
  // ============================================================
 function domRenderSavingsBreakdown(cart) {
  const existing = document.querySelector('.cart-savings-breakdown-block');
  if (existing) existing.remove();
  const savingsItems = []; let totalSavings = 0;
  cart.items.forEach(item => {
    if (!item.selling_plan_allocation) return;
    const compareAt   = item.selling_plan_allocation.compare_at_price;
    const actualPrice = item.selling_plan_allocation.price;
    if (!compareAt || compareAt <= actualPrice) return;
    const savingPerDelivery = (compareAt - actualPrice) * item.quantity;  // ✅ per delivery
    const savingFor3        = savingPerDelivery * 3;                       // ✅ full 3 deliveries
    savingsItems.push({ title: item.product_title, savingPerDelivery, savingFor3 });
    totalSavings += savingFor3;
  });
  if (savingsItems.length === 0) return;
  const itemCardsHTML = savingsItems.map(s => `
    <div class="csb-item">
      <p class="csb-item__title">${s.title}</p>
      <div class="csb-item__row"><span>Box savings per delivery:</span><span class="csb-item__value">${formatMoney(s.savingPerDelivery)}</span></div>
      <div class="csb-item__row"><span>Number of deliveries:</span><span>3</span></div>
      <div class="csb-item__row csb-item__row--total"><span>Total savings:</span><span class="csb-item__value">${formatMoney(s.savingFor3)}</span></div>
    </div>`).join('');
  const block = document.createElement('div');
  block.className = 'cart-savings-breakdown-block';
  block.innerHTML = `
    <div class="csb-banner">
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="10" stroke="#e24e27" stroke-width="2"/>
        <path d="M12 8v4M12 16h.01" stroke="#e24e27" stroke-width="2" stroke-linecap="round"/>
      </svg>
      <div>
        <p class="csb-banner__title">You're saving ${formatMoney(totalSavings)} with subscription delivery on your next 3 orders and savings on continued deliveries.</p>
        <p class="csb-banner__sub">You can skip, pause, or modify your delivery schedule anytime</p>
      </div>
    </div>
    <button type="button" class="csb-toggle" aria-expanded="false">
      <span>View Savings Breakdown</span>
      <svg class="csb-toggle__icon" width="20" height="20" viewBox="0 0 24 24" fill="none">
        <path d="M6 9L12 15L18 9" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    </button>
    <div class="csb-content" style="display:none;height:0;opacity:0;overflow:hidden;">
      ${itemCardsHTML}
      <div class="csb-grand-total"><span>Total Subscription Savings:</span><span>${formatMoney(totalSavings)}</span></div>
    </div>`;
  const btn = block.querySelector('.csb-toggle'), content = block.querySelector('.csb-content');
  btn.addEventListener('click', function() {
    const isExpanded = this.getAttribute('aria-expanded') === 'true';
    if (isExpanded) {
      this.setAttribute('aria-expanded', 'false');
      content.style.height = content.scrollHeight + 'px';
      requestAnimationFrame(() => { content.style.height = '0px'; content.style.opacity = '0'; });
      setTimeout(() => { content.style.display = 'none'; content.style.height = ''; }, 300);
    } else {
      this.setAttribute('aria-expanded', 'true');
      content.style.display = 'block'; content.style.height = '0px'; content.style.opacity = '0';
      requestAnimationFrame(() => { content.style.height = content.scrollHeight + 'px'; content.style.opacity = '1'; });
      setTimeout(() => { content.style.height = 'auto'; }, 300);
    }
  });
  const benefits = document.querySelector('.cart__benefits');
  if (benefits) benefits.insertAdjacentElement('beforebegin', block);
}

})();